import React, { createContext, useCallback, useEffect, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';
import { Assignment, Group } from '@monorepo/shared/src/graph/schema-generated-types-educator';
import { AppRoutes } from 'types';
import UseAssignmentStore from 'graph/state/query-hooks/use-assignments-store';
import useAssignmentSuccessAlert from 'state/hooks/use-assignment-success-alert';
import useClassrooms from 'state/hooks/use-classrooms';
import { defaultState, reducers } from './reducers';
import { Actions, UseAssignmentState } from './types';
import {
  findAssignment,
  getUpdatedGroups,
  getUpdatedStudents,
  removeStudentDuplicates,
  getStudentsAndGroupsFromAssignments,
} from './utils';
import useTeachingOrder from 'state/hooks/use-teaching-order';
import { useRouteGuard } from 'state/hooks/use-route-guard';

export const AssignmentContext = createContext<any>([]);

export const AssignmentsProvider: React.FC = ({ children }) => {
  const [state, dispatch] = useReducer<React.Reducer<UseAssignmentState, Actions>>(
    reducers,
    defaultState()
  );
  const { blockRouter, unblockRouter, isRouterBlocked } = useRouteGuard();
  const [{ activeCharacters }] = useTeachingOrder();
  const [{ activeClassroom }] = useClassrooms();
  const [
    { assignments, isLoading, error },
    {
      handleUpdateAssignment,
      handleDeleteAssignment,
      handleCreateAssignment,
      refetch: assignmentsRefetch,
    },
  ] = UseAssignmentStore();

  const navigate = useNavigate();

  const { handleSuccessSaveAlert } = useAssignmentSuccessAlert(state.letter);

  const deduplicateStudentsAndGroups = () => {
    const { selectedStudents, allGroups } = state;
    return removeStudentDuplicates(selectedStudents, allGroups);
  };

  const setAssignments = useCallback((assignments: Assignment[]) => {
    dispatch({ type: 'SET_ASSIGNMENTS', payload: { assignments } });
  }, []);

  const setAllGroups = useCallback((groups: Group[]) => {
    if (!groups) return;
    dispatch({ type: 'SET_ALL_GROUPS', payload: { groups } });
  }, []);

  const setSaveSuccess = (wasSuccessful) => {
    dispatch({ type: 'SET_SAVE_SUCCESS', payload: { saveSuccessful: wasSuccessful } });
  };

  const setLetter = useCallback((letter: string) => {
    dispatch({ type: 'SET_LETTER', payload: { letter } });
  }, []);

  const setGroupFilter = useCallback((groupFilter: string) => {
    dispatch({ type: 'SET_GROUP_FILTER', payload: { groupFilter } });
  }, []);

  const setSelectedGroupsAndStudents = useCallback(() => {
    const { selectedGroups, selectedStudents } = getStudentsAndGroupsFromAssignments(
      state.assignments,
      state.letter
    );

    dispatch({
      type: 'SET_SELECTED_GROUPS_AND_STUDENTS',
      payload: { selectedGroups, selectedStudents },
    });
  }, [state.assignments, state.letter]);

  const updateSelectedStudents = useCallback(
    (studentId: number) => {
      const { updatedStudents, updatedGroups } = getUpdatedStudents(
        state.selectedStudents,
        studentId,
        state.allGroups
      );
      dispatch({
        type: 'SET_SELECTED_GROUPS_AND_STUDENTS',
        payload: {
          selectedGroups: updatedGroups,
          selectedStudents: updatedStudents,
          hasUnsavedChanges: true,
        },
      });
      blockRouter();
    },
    [state.selectedStudents, state.selectedGroups, state.allGroups]
  );

  const updateSelectedGroups = useCallback(
    (groupId: number) => {
      const { updatedGroups, updatedStudents } = getUpdatedGroups(
        groupId,
        state.selectedGroups,
        state.allGroups,
        state.selectedStudents
      );
      dispatch({
        type: 'SET_SELECTED_GROUPS_AND_STUDENTS',
        payload: {
          selectedGroups: updatedGroups,
          selectedStudents: updatedStudents,
          hasUnsavedChanges: true,
        },
      });
      blockRouter();
    },
    [state.selectedGroups, state.selectedStudents, state.allGroups, state.selectedStudents]
  );

  const updateAssignments = useCallback(
    async (selectionOverrides?: { selectedGroups: number[]; selectedStudents: number[] }) => {
      const { assignments, selectedGroups, selectedStudents, allGroups, letter } = state;
      const assignment = findAssignment(assignments, letter);
      if (assignment?.id) {
        const groups = selectionOverrides?.selectedGroups ?? selectedGroups;
        const students = selectionOverrides?.selectedStudents ?? selectedStudents;
        const studentIds = removeStudentDuplicates(students, allGroups);
        const filteredGroups = groups?.filter((x) => x);
        const filteredStudents = studentIds?.filter((x) => x);
        if (!filteredStudents?.length && !filteredGroups?.length) {
          await handleDeleteAssignment(assignment?.id);
          await assignmentsRefetch();
        } else {
          await handleUpdateAssignment({
            id: assignment?.id,
            ...(filteredGroups?.length && { groupIds: filteredGroups }),
            ...(filteredStudents?.length && { studentIds: filteredStudents }),
          });
          await assignmentsRefetch();
        }
      }
    },
    [state.assignments, state.selectedGroups, state.selectedStudents, state.allGroups, state.letter]
  );

  const createAssignment = useCallback(async () => {
    const { selectedGroups, selectedStudents, allGroups, letter } = state;
    const studentIds = removeStudentDuplicates(selectedStudents, allGroups);
    const character = activeCharacters.find(
      (char) => char.character.toLowerCase() === letter.toLowerCase()
    );
    if (character?.id && activeClassroom?.id) {
      const filteredGroups = selectedGroups.filter((x) => x);
      const filteredStudents = studentIds.filter((x) => x);
      return await handleCreateAssignment({
        characterId: character?.id,
        classroomId: activeClassroom?.id,
        ...(filteredGroups?.length && { groupIds: filteredGroups }),
        ...(filteredStudents?.length && { studentIds: filteredStudents }),
      });
    }
    return;
  }, [
    state.assignments,
    state.selectedGroups,
    state.selectedStudents,
    state.allGroups,
    state.letter,
    activeClassroom,
  ]);

  const handleExit = () => {
    setTimeout(() => {
      navigate(`/${AppRoutes.ASSIGNMENTS}`);
    });
  };

  const handleEditSave = useCallback(async () => {
    await updateAssignments();
    unblockRouter();
    handleExit();
  }, [state.assignments, state.selectedGroups, state.selectedStudents]);

  const handleNewAssignmentSave = useCallback(async () => {
    const assignment = await createAssignment();
    const wasSuccessful = assignment && !error;
    setSaveSuccess(wasSuccessful);
    unblockRouter();
    await assignmentsRefetch();
    handleExit();
  }, [state.assignments, state.selectedGroups, state.selectedStudents]);

  const unassignAll = useCallback(async () => {
    const clearedSelections = { selectedGroups: [], selectedStudents: [] };
    dispatch({
      type: 'SET_SELECTED_GROUPS_AND_STUDENTS',
      payload: clearedSelections,
    });
    await updateAssignments(clearedSelections);
  }, [state.selectedGroups, state.selectedStudents, state.assignments, state.letter]);

  useEffect(() => {
    if (!isLoading && !error) {
      setAssignments(assignments);
    }
  }, [assignments]);

  useEffect(() => {
    if (state.saveSuccessful) handleSuccessSaveAlert();
  }, [state.saveSuccessful]);

  // if user makes changes that resets it to original assignments
  // set unsavedChanges to false
  useEffect(() => {
    const { assignments, letter, selectedGroups, selectedStudents } = state;
    const { selectedGroups: previousGroups, selectedStudents: previousStudents } =
      getStudentsAndGroupsFromAssignments(assignments, letter);

    const areGroupsEqual = JSON.stringify(selectedGroups) === JSON.stringify(previousGroups);
    const areStudentsEqual = JSON.stringify(selectedStudents) === JSON.stringify(previousStudents);

    if (areStudentsEqual && areGroupsEqual) {
      if (isRouterBlocked) unblockRouter();
      dispatch({ type: 'SET_UNSAVED_CHANGES', payload: { hasUnsavedChanges: false } });
    }
  }, [state.selectedGroups, state.selectedStudents]);

  return (
    <AssignmentContext.Provider
      value={[
        state,
        {
          setLetter,
          setAssignments,
          deduplicateStudentsAndGroups,
          handleEditSave,
          handleNewAssignmentSave,
          updateSelectedGroups,
          setSelectedGroupsAndStudents,
          updateSelectedStudents,
          setAllGroups,
          unassignAll,
          handleExit,
          setGroupFilter,
        },
      ]}
    >
      {children}
    </AssignmentContext.Provider>
  );
};

export default AssignmentsProvider;
